import { useCallback, useEffect, useRef, useState } from 'react';
import { initiateOnboardingAuth, getConnectionStatus } from '../../../services/onboardingApi';
import type { ProviderInfo } from '../providerCatalog';

export interface ConnectFlowCallbacks {
  onSuccess?: (slug: string) => void;
  onCloseManage?: () => void;
}

export function useComposioConnectFlow(
  clientId: string | null | undefined,
  onConnected?: (slug: string) => void,
) {
  const [connectingSlug, setConnectingSlug] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const busyRef = useRef(false);
  const popupRef = useRef<Window | null>(null);
  // Set while a flow is in-flight; calling it resolves the flow as cancelled
  // (closes the popup, tears down listeners). Lets the user close the modal.
  const abortRef = useRef<(() => void) | null>(null);

  const clearConnectError = useCallback(() => setConnectError(null), []);

  const cancelConnectFlow = useCallback(() => {
    abortRef.current?.();
  }, []);

  const runConnectFlow = useCallback(async (
    provider: ProviderInfo,
    callbacks: ConnectFlowCallbacks = {},
  ): Promise<boolean> => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setConnectError(null);
    setConnectingSlug(provider.slug);

    const popup = window.open('about:blank', '_blank', 'noopener=no,width=560,height=720');
    popupRef.current = popup;
    if (popup && !popup.closed) {
      try {
        popup.document.write(`
          <!doctype html><html><head><meta charset="utf-8"><title>Connecting ${provider.label}…</title>
          <style>
            body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;margin:0;padding:3rem;text-align:center;background:#EEF3FF;color:#111827}
            h2{font-size:18px;font-weight:600;margin:0 0 .5rem}
            p{color:#6B7280;font-size:14px;line-height:1.5}
            .spinner{width:28px;height:28px;border:3px solid #E5E7EB;border-top-color:#18191d;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 1rem}
            @keyframes spin{to{transform:rotate(360deg)}}
          </style>
          </head><body><div class="spinner"></div><h2>Connecting ${provider.label}…</h2><p>Complete authorization in this window. You can return here when done.</p></body></html>
        `);
      } catch { /* ignore */ }
    }

    const closePopup = () => {
      const p = popupRef.current;
      if (p && !p.closed) { try { p.close(); } catch { /* ignore */ } }
    };

    let composioClientId: string | undefined;
    try {
      const res = await initiateOnboardingAuth({ provider: provider.slug, clientId: clientId || undefined });
      composioClientId = res.composioClientId;

      if (res.alreadyConnected) {
        closePopup();
        callbacks.onCloseManage?.();
        callbacks.onSuccess?.(provider.slug);
        onConnected?.(provider.slug);
        return true;
      }

      if (!res.redirectUrl) {
        closePopup();
        return false;
      }

      if (popup && !popup.closed) {
        try { popup.location.href = res.redirectUrl; } catch { /* ignore */ }
      }
    } catch (err: any) {
      // Surface the real reason instead of a silent popup-flash. The backend
      // passes through Composio's message as the error detail (e.g. an app
      // that has no Composio auth config set up), so the user can see what
      // actually went wrong rather than the flow just dying.
      closePopup();
      setConnectError(
        err?.message?.replace(/^initiate-auth \d+:\s*/, '')
        || 'Could not start authorization. Please try again.',
      );
      setConnectingSlug(null);
      busyRef.current = false;
      popupRef.current = null;
      return false;
    }

    // Wait for completion. Primary signal is the postMessage our backend
    // connection-callback page emits the instant the OAuth round-trip lands
    // (window.opener.postMessage({type:'composio:connected', ...})) — this is
    // immediate and immune to background-tab timer throttling. Polling is only
    // a fallback for the case where the opener reference is severed (COOP) and
    // postMessage never arrives.
    const POLL_INTERVAL_MS = 2000;
    const POPUP_OPEN_TIMEOUT_MS = 3 * 60 * 1000;
    // Generous grace after the popup closes so a slightly-lagging Composio
    // status still resolves to success instead of snapping back to "Authorize".
    const POPUP_CLOSED_GRACE_MS = 20 * 1000;

    const result = await new Promise<boolean>((resolve) => {
      let settled = false;
      let popupClosedAt: number | null = null;
      const startedAt = Date.now();

      const finish = (val: boolean) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        clearInterval(poll);
        abortRef.current = null;
        resolve(val);
      };

      const succeed = () => { closePopup(); finish(true); };

      const checkStatus = async () => {
        if (settled) return;
        try {
          const status = await getConnectionStatus({
            provider: provider.slug,
            composioClientId,
            clientId: clientId || undefined,
          });
          const ok = status.connected === true || status.active === true
            || String(status.status || '').toUpperCase() === 'ACTIVE';
          if (ok) succeed();
        } catch { /* keep waiting */ }
      };

      // Primary: the callback page tells us directly.
      const onMessage = (e: MessageEvent) => {
        const d: any = e?.data;
        if (!d || d.type !== 'composio:connected') return;
        if (d.provider && d.provider !== provider.slug) return;
        if (d.connected) succeed();
        else checkStatus(); // callback couldn't confirm yet — verify now
      };
      window.addEventListener('message', onMessage);

      // User closed the modal / hit Escape / cancelled.
      abortRef.current = () => { closePopup(); finish(false); };

      // Fallback: poll, and react immediately when the popup closes.
      const poll = setInterval(() => {
        if (settled) return;
        if (popupRef.current && popupRef.current.closed && popupClosedAt === null) {
          popupClosedAt = Date.now();
          checkStatus(); // don't wait a full interval after the window closes
        }
        checkStatus();
        if (popupClosedAt !== null && Date.now() - popupClosedAt > POPUP_CLOSED_GRACE_MS) {
          finish(false);
        }
        if (Date.now() - startedAt > POPUP_OPEN_TIMEOUT_MS) {
          closePopup();
          finish(false);
        }
      }, POLL_INTERVAL_MS);
    });

    setConnectingSlug(null);
    busyRef.current = false;
    popupRef.current = null;

    if (result) {
      callbacks.onCloseManage?.();
      callbacks.onSuccess?.(provider.slug);
    }
    // Refresh the connection list on every exit — even when we couldn't confirm
    // success in-flow, the callback may have already marked it connected
    // server-side, so this keeps the button state honest.
    onConnected?.(provider.slug);
    return result;
  }, [clientId, onConnected]);

  // Tear down any in-flight flow if the component unmounts.
  useEffect(() => () => { abortRef.current?.(); }, []);

  return {
    runConnectFlow,
    cancelConnectFlow,
    connectingSlug,
    isConnecting: !!connectingSlug,
    connectError,
    clearConnectError,
  };
}
